import { DEFAULT_TIMEZONE, LIMA_UTC_OFFSET_HOURS, NULL_DATE_SENTINELS } from '../constants';

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

/** ¿Es una zona IANA válida para Intl? */
export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Desfase (ms) de la zona respecto a UTC en un instante dado. Lima (UTC-5, sin horario de verano) se resuelve
 * sin Intl; el resto de zonas usa Intl.DateTimeFormat (respeta horario de verano).
 */
export function tzOffsetMs(date: Date, tz: string = DEFAULT_TIMEZONE): number {
  if (tz === DEFAULT_TIMEZONE) return LIMA_UTC_OFFSET_HOURS * HOUR_MS;
  let f = offsetFormatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    offsetFormatters.set(tz, f);
  }
  const p: Record<string, number> = {};
  for (const part of f.formatToParts(date))
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  const wall = Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!);
  return wall - Math.floor(date.getTime() / 1000) * 1000;
}

/** Convierte un instante a la "hora de pared" de la zona, expresada como fecha UTC. */
function toWall(date: Date, tz: string): Date {
  return new Date(date.getTime() + tzOffsetMs(date, tz));
}

/** Instante UTC que corresponde a una hora de pared (expresada como Date UTC) en la zona. */
function fromWall(wall: Date, tz: string): Date {
  // Primera aproximación con el desfase de la propia pared; se corrige si cae en un cambio de horario.
  const guess = new Date(wall.getTime() - tzOffsetMs(wall, tz));
  return new Date(wall.getTime() - tzOffsetMs(guess, tz));
}

/** Fecha pura 'YYYY-MM-DD' (sin hora), tal como la elige la persona en un `<input type="date">`. */
export const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
export function isDayKey(value: unknown): value is string {
  return typeof value === 'string' && DAY_KEY_RE.test(value);
}

/** ¿Es una fecha pura 'YYYY-MM-DD' que existe en el calendario (no '2026-02-30')? */
export function isValidDayKey(value: unknown): value is string {
  if (!isDayKey(value)) return false;
  const wall = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(wall.getTime()) && wall.toISOString().slice(0, 10) === value;
}

/** Inicio (00:00 de la zona) de una fecha pura 'YYYY-MM-DD', como instante UTC. Lanza si la fecha no es válida. */
export function startOfDayKey(day: string, tz: string = DEFAULT_TIMEZONE): Date {
  if (!isValidDayKey(day)) throw new Error(`Fecha inválida: ${day}`);
  return fromWall(new Date(`${day}T00:00:00.000Z`), tz);
}

/** Primer instante del día siguiente a una fecha pura (límite superior exclusivo de un «hasta» inclusivo). */
export function startOfNextDayKey(day: string, tz: string = DEFAULT_TIMEZONE): Date {
  return fromWall(new Date(startOfDayKey(day, 'UTC').getTime() + DAY_MS), tz);
}

/** Límite de un filtro de fechas: una fecha pura 'YYYY-MM-DD' (día completo en la zona) o un instante con hora. */
export type DateBound = string | Date | null | undefined;

/**
 * Regla única de los filtros «desde / hasta» (ux-listado-03, api-comercial-embudo-01): una fecha pura se resuelve
 * como el inicio del día en la zona de la empresa y el «hasta» es inclusivo (el límite es el inicio del día
 * siguiente); un instante con hora se usa tal cual (`gte` / `lt`).
 */
export function dayBounds(
  from: DateBound,
  to: DateBound,
  tz: string = DEFAULT_TIMEZONE,
): { gte?: Date; lt?: Date } {
  const out: { gte?: Date; lt?: Date } = {};
  if (from) out.gte = typeof from === 'string' ? startOfDayKey(from, tz) : from;
  if (to) out.lt = typeof to === 'string' ? startOfNextDayKey(to, tz) : to;
  return out;
}

/** Componentes de la hora de pared del instante en la zona. */
function wallParts(date: Date, tz: string) {
  const w = toWall(date, tz);
  return {
    year: w.getUTCFullYear(),
    month: w.getUTCMonth() + 1,
    day: w.getUTCDate(),
    hour: w.getUTCHours(),
    minute: w.getUTCMinutes(),
  };
}
const pad2 = (n: number) => String(n).padStart(2, '0');

/** Valor para `<input type="datetime-local">` ('YYYY-MM-DDTHH:mm') con la hora de pared de la zona. */
export function toWallInput(date: Date, tz: string = DEFAULT_TIMEZONE): string {
  const p = wallParts(date, tz);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** Instante UTC de un valor de `datetime-local` ('YYYY-MM-DDTHH:mm[:ss]') leído como hora de pared de la zona. */
export function fromWallInput(value: string, tz: string = DEFAULT_TIMEZONE): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(value);
  if (!m) throw new Error(`Fecha y hora inválidas: ${value}`);
  const wall = new Date(
    Date.UTC(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4] ?? 0),
      Number(m[5] ?? 0),
      Number(m[6] ?? 0),
    ),
  );
  if (Number.isNaN(wall.getTime())) throw new Error(`Fecha y hora inválidas: ${value}`);
  return fromWall(wall, tz);
}

/** 'dd/mm/aaaa' del instante en la zona (textos y exportaciones). */
export function formatDayDMY(date: Date, tz: string = DEFAULT_TIMEZONE): string {
  const p = wallParts(date, tz);
  return `${pad2(p.day)}/${pad2(p.month)}/${p.year}`;
}
/** 'hh:mm' (0–23) del instante en la zona. */
export function formatTimeHM(date: Date, tz: string = DEFAULT_TIMEZONE): string {
  const p = wallParts(date, tz);
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}
/** 'dd/mm/aaaa hh:mm' del instante en la zona (ciclo de 24 h, sin «24:00»). */
export function formatDateTimeDMY(date: Date, tz: string = DEFAULT_TIMEZONE): string {
  return `${formatDayDMY(date, tz)} ${formatTimeHM(date, tz)}`;
}

/** 'YYYY-MM' del instante en la zona de negocio (Lima por defecto). */
export function monthKey(date: Date, tz: string = DEFAULT_TIMEZONE): string {
  return toWall(date, tz).toISOString().slice(0, 7);
}

/** 'YYYY-MM-DD' del instante en la zona de negocio. */
export function dateKey(date: Date, tz: string = DEFAULT_TIMEZONE): string {
  return toWall(date, tz).toISOString().slice(0, 10);
}

/** Inicio del día (00:00 de la zona) que contiene el instante, como instante UTC. */
export function startOfLimaDay(date: Date, tz: string = DEFAULT_TIMEZONE): Date {
  const wall = toWall(date, tz);
  return fromWall(
    new Date(Date.UTC(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate())),
    tz,
  );
}
/** Alias con nombre neutro: inicio del día de negocio en la zona de la empresa. */
export const startOfBusinessDay = startOfLimaDay;

/** Inicio del mes (día 1, 00:00 de la zona) como instante UTC. */
export function startOfLimaMonth(date: Date, tz: string = DEFAULT_TIMEZONE): Date {
  const wall = toWall(date, tz);
  return fromWall(new Date(Date.UTC(wall.getUTCFullYear(), wall.getUTCMonth(), 1)), tz);
}

/** Primer instante del mes siguiente. Útil como límite superior exclusivo. */
export function startOfNextLimaMonth(date: Date, tz: string = DEFAULT_TIMEZONE): Date {
  const wall = toWall(date, tz);
  return fromWall(new Date(Date.UTC(wall.getUTCFullYear(), wall.getUTCMonth() + 1, 1)), tz);
}

/** Rango [from, to) de un mes 'YYYY-MM' en la zona de negocio. */
export function monthRange(month: string, tz: string = DEFAULT_TIMEZONE): { from: Date; to: Date } {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) throw new Error(`Mes inválido: ${month}`);
  return {
    from: fromWall(new Date(Date.UTC(y, m - 1, 1)), tz),
    to: fromWall(new Date(Date.UTC(y, m, 1)), tz),
  };
}

/** Días calendario (zona de negocio) entre dos instantes: floor((díaB − díaA)). Negativo si b < a. */
export function limaDaysBetween(a: Date, b: Date, tz: string = DEFAULT_TIMEZONE): number {
  return Math.round((startOfLimaDay(b, tz).getTime() - startOfLimaDay(a, tz).getTime()) / DAY_MS);
}

/** Hora local (0–23) del instante en la zona. */
export function hourIn(date: Date, tz: string = DEFAULT_TIMEZONE): number {
  return toWall(date, tz).getUTCHours();
}

/** Las fechas centinela (1900-01-01) del sistema anterior se tratan como nulas. */
export function isNullDate(date: Date | string | null | undefined): boolean {
  if (date === null || date === undefined) return true;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return true;
  return NULL_DATE_SENTINELS.includes(d.toISOString().slice(0, 10));
}

export function cleanDate(date: Date | string | null | undefined): Date | null {
  if (isNullDate(date)) return null;
  return typeof date === 'string' ? new Date(date!) : (date as Date);
}

/** % del mes transcurrido a la fecha (0–1) para comparar real vs meta. */
export function monthElapsedRatio(now: Date, tz: string = DEFAULT_TIMEZONE): number {
  const start = startOfLimaMonth(now, tz).getTime();
  const end = startOfNextLimaMonth(now, tz).getTime();
  const elapsed = startOfLimaDay(now, tz).getTime() + DAY_MS - start;
  return Math.min(1, Math.max(0, elapsed / (end - start)));
}

/** Tope del rango de meses del embudo (desde – hasta). */
export const MAX_FUNNEL_MONTHS = 24;

/** Meses 'YYYY-MM' entre dos meses, ambos incluidos (vacío si el final es anterior al inicio). */
export function monthsBetween(from: string, to: string): string[] {
  const parse = (m: string) => {
    const [y, mm] = m.split('-').map(Number);
    if (!y || !mm || mm < 1 || mm > 12) throw new Error(`Mes inválido: ${m}`);
    return y * 12 + (mm - 1);
  };
  const a = parse(from);
  const b = parse(to);
  const out: string[] = [];
  for (let i = a; i <= b; i++)
    out.push(`${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`);
  return out;
}

/** Lista 'YYYY-MM' desde texto separado por comas: sin repetidos, ordenada; lanza si alguno es inválido. */
export function parseMonthList(raw: string): string[] {
  const out = new Set<string>();
  for (const part of raw.split(',')) {
    const m = part.trim();
    if (!m) continue;
    if (!/^\d{4}-\d{2}$/.test(m)) throw new Error(`Mes inválido: ${m}`);
    monthsBetween(m, m);
    out.add(m);
  }
  return [...out].sort();
}

/** true si los meses (ordenados) van seguidos, sin saltos. */
export function areConsecutiveMonths(months: readonly string[]): boolean {
  if (months.length <= 1) return true;
  return monthsBetween(months[0]!, months[months.length - 1]!).length === months.length;
}

/** Desplaza un mes 'YYYY-MM' en delta meses (negativo = hacia atrás). */
export function shiftMonthKey(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y!, m! - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
